import { useState } from "react";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQContent {
  badge?: string;
  headline: string;
  subtext: string;
  faqs: FAQItem[];
}

interface FAQSectionProps {
  content: FAQContent;
}

export default function FAQSection({ content }: FAQSectionProps) {
  const { badge, headline, subtext, faqs } = content;
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const ref = useScrollRevealChildren();

  return (
    <section className="bg-white py-28 overflow-hidden">
      <div ref={ref} className="max-w-screen-xl mx-auto px-6 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 xl:gap-24">

          {/* Left: Sticky header panel */}
          <div className="lg:col-span-2">
            {badge && (
              <div className="reveal flex items-center gap-4 mb-7">
                <div className="h-px w-12 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[56px] text-[#1C1C1C] leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              {headline}
            </h2>
            <p
              className="reveal delay-200 text-[#777] text-[15px] leading-relaxed mb-10"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              {subtext}
            </p>
            <div className="reveal delay-300 space-y-3">
              <a
                href="tel:5614416008"
                className="flex items-center gap-3 bg-[#1C1C1C] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-8 py-4 hover:bg-[#C9A961] transition-all duration-400 cursor-pointer whitespace-nowrap w-fit"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-sm" />
                </div>
                (561) 441-6008
              </a>
              <p className="text-[#BBB] text-[11px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Mon–Fri 10AM–5PM · Boca Raton Showroom
              </p>
            </div>

            {/* Decorative line element */}
            <div className="mt-12 flex items-center gap-4">
              <div className="h-px flex-1 bg-[#F0EBE2]" />
              <div className="w-1.5 h-1.5 bg-[#C9A961] rotate-45 flex-shrink-0" />
              <div className="h-px flex-1 bg-[#F0EBE2]" />
            </div>
          </div>

          {/* Right: FAQ accordion */}
          <div className="lg:col-span-3">
            <div className="divide-y divide-[#F5F0E8]">
              {faqs.map((faq, idx) => (
                <div key={idx} className={`reveal delay-${Math.min(idx * 60 + 100, 600)} py-7`}>
                  <button
                    onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                    className="w-full flex items-start justify-between gap-6 text-left cursor-pointer group"
                  >
                    <h3
                      className={`text-[15px] font-semibold leading-relaxed transition-colors duration-300 ${
                        openIdx === idx ? "text-[#C9A961]" : "text-[#1C1C1C] group-hover:text-[#C9A961]"
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {faq.question}
                    </h3>
                    <div
                      className={`w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 ${
                        openIdx === idx
                          ? "bg-[#C9A961] text-white border-[#C9A961]"
                          : "border border-[#E8E2D9] text-[#BBB] group-hover:border-[#C9A961] group-hover:text-[#C9A961]"
                      }`}
                    >
                      <i
                        className={`text-[11px] transition-transform duration-300 ${
                          openIdx === idx ? "ri-subtract-line" : "ri-add-line"
                        }`}
                      />
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      openIdx === idx ? "max-h-96 opacity-100 mt-5" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p
                      className="text-[#666] text-sm leading-[2]"
                      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                    >
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
